import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled, { keyframes } from 'styled-components';
import config from '../../config';
import { useAuthContext } from '../../auth/AuthContext';
import type { Question } from '../../config/types';
import { queryAthenaLookup } from '../../service/form';
import type { AthenaLookupEnvironment, AthenaLookupRow } from '../../service/types';
import { getInstancePrefix } from '../../utils/autofill-math';
import AutocompleteWrapper from './Autocomplete/AutocompleteWrapper';

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

export type StaffLookupRow = {
  staff_number: string;
  cost_centre: string;
  name: string;
  grade: string;
  email_address: string;
  directorate: string;
  contract_type: string;
  line_manager_email: string;
  leaving_date: string;
};

const MINIMUM_SEARCH_LENGTH = 2;
const DEFAULT_MATCH_LIMIT = 5;
const LOOKUP_RESULT_LIMIT = 25;
const LOOKUP_LOADING_MESSAGE = 'Searching records...';
const MISSING_EMPLOYEE_IDENTIFIER_ERROR =
  'We are unable to find the CO SOP Employee identifier for this person, please contact co-digital-peopleandbusinessservices@cabinetoffice.gov.uk if you think this is wrong';
const NON_CIVIL_SERVANT_ERROR =
  'Our records indicate this person may not be a Civil Servant. Contractors and on loan staff, etc are not eligible. Please contact co-digital-peopleandbusinessservices@cabinetoffice.gov.uk if you think this is wrong';
const LEFT_BUSINESS_ERROR =
  'Our records indicate this person may have left the business. Please contact co-digital-peopleandbusinessservices@cabinetoffice.gov.uk if you think this is wrong';
const SELF_NOMINATION_ERROR = 'Sorry, you cannot nominate yourself.';
const DUPLICATE_NOMINEE_ERROR =
  'Duplicate nominee: This person is already included in this nomination.';
const toNonEmptyString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const SCS_REWARD_ERROR = 'This form cannot be used for SCS rewards.';
const GOOD_CHOICE_EMAIL = 'peter.cunningham@cabinetoffice.gov.uk';
const GOOD_CHOICE_MESSAGE = 'Great choice 👍';
const LOOKUP_SCROLL_DELAY_MS = 2000;
const LOOKUP_SCROLL_DURATION_MS = 500;
const GOOD_CHOICE_CONFETTI_DURATION_MS = 1400;
const GOOD_CHOICE_CONFETTI_PARTICLES = [
  { left: '4%', color: '#d4351c', width: 6, height: 12, delayMs: 0, driftPx: -20, spinDeg: -80 },
  { left: '12%', color: '#ffdd00', width: 8, height: 10, delayMs: 90, driftPx: -12, spinDeg: 50 },
  { left: '22%', color: '#1d70b8', width: 6, height: 14, delayMs: 40, driftPx: -6, spinDeg: -120 },
  { left: '34%', color: '#00703c', width: 7, height: 11, delayMs: 120, driftPx: 4, spinDeg: 70 },
  { left: '48%', color: '#f47738', width: 8, height: 12, delayMs: 10, driftPx: -2, spinDeg: -60 },
  { left: '58%', color: '#912b88', width: 6, height: 10, delayMs: 150, driftPx: 10, spinDeg: 110 },
  { left: '68%', color: '#d53880', width: 7, height: 12, delayMs: 70, driftPx: 16, spinDeg: -90 },
  { left: '78%', color: '#0b0c0c', width: 6, height: 9, delayMs: 180, driftPx: 18, spinDeg: 45 },
  { left: '88%', color: '#1d70b8', width: 8, height: 11, delayMs: 110, driftPx: 12, spinDeg: -75 },
  { left: '96%', color: '#00703c', width: 6, height: 12, delayMs: 30, driftPx: 22, spinDeg: 95 },
] as const;

const confettiBurst = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(0, -4px, 0) scale(0.6) rotate(0deg);
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate3d(var(--confetti-drift), 40px, 0) scale(1) rotate(var(--confetti-spin));
  }
`;

const CelebrationMessage = styled.div`
  position: relative;
  display: inline-block;
  overflow: visible;
`;

const CelebrationConfetti = styled.span`
  pointer-events: none;
  position: absolute;
  left: -8px;
  right: -8px;
  top: -10px;
  height: 56px;
  display: block;
  overflow: visible;
`;

const CelebrationConfettiPiece = styled.span<{
  $left: string;
  $color: string;
  $width: number;
  $height: number;
  $delayMs: number;
  $driftPx: number;
  $spinDeg: number;
}>`
  position: absolute;
  top: 0;
  left: ${(props) => props.$left};
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  border-radius: 2px;
  background: ${(props) => props.$color};
  opacity: 0;
  animation: ${confettiBurst} 900ms ease-out forwards;
  animation-delay: ${(props) => props.$delayMs}ms;
  --confetti-drift: ${(props) => `${props.$driftPx}px`};
  --confetti-spin: ${(props) => `${props.$spinDeg}deg`};
`;

export const getLookupTargetFields = (questionName: string) => {
  const prefix = getInstancePrefix(questionName);
  return {
    staffNumber: `${prefix}nominee-staff-number`,
    costCentre: `${prefix}nominee-cost-centre`,
    fullName: `${prefix}nominee-full-name`,
    email: `${prefix}nominee-email`,
    grade: `${prefix}nominee-grade`,
    function: `${prefix}nominee-function`,
    lineManagerEmail: `${prefix}nominee-line-manager-email`,
    dataCorrect: `${prefix}nominee-data-correct`,
    incorrectReason: `${prefix}nominee-data-incorrect-reason`,
  };
};

const easeInOutCubic = (progress: number): number =>
  progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - ((-2 * progress + 2) ** 3) / 2;

const animateWindowScrollTo = (targetTop: number) => {
  if (typeof window === 'undefined') return;
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) return;

  const startTop = window.scrollY;
  const distance = targetTop - startTop;
  if (Math.abs(distance) < 1) return;

  const startTime = performance.now();

  const step = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / LOOKUP_SCROLL_DURATION_MS, 1);
    const easedProgress = easeInOutCubic(progress);

    window.scrollTo(window.scrollX, startTop + distance * easedProgress);

    if (progress < 1) {
      window.requestAnimationFrame(step);
    }
  };

  window.requestAnimationFrame(step);
};

export const scrollToFieldGroup = (fieldName: string) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  window.setTimeout(() => {
    const input = document.querySelector<HTMLInputElement>(`input[name="${fieldName}"]`);
    const fieldGroup = input?.closest('.govuk-form-group');
    if (!fieldGroup) return;

    const targetTop = Math.max(0, window.scrollY + fieldGroup.getBoundingClientRect().top);
    animateWindowScrollTo(targetTop);
  }, LOOKUP_SCROLL_DELAY_MS);
};

const getRuntimeEnvironment = (): AthenaLookupEnvironment => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const apiUrl = String(config.apiUrl || '').toLowerCase();
  return hostname.includes('preprod') || apiUrl.includes('preprod') ? 'preprod' : 'prod';
};

const formatLookupLabel = (row: StaffLookupRow): string =>
  `${row.name} (${row.email_address})`;

const normalizeLookupValue = (value: string): string => value.trim().toLowerCase();

export const resolveSelectedLookupRow = (
  value: string,
  rowsByLabel: Record<string, StaffLookupRow>,
): StaffLookupRow | null => {
  const directMatch = rowsByLabel[value];
  if (directMatch) return directMatch;

  const normalizedValue = normalizeLookupValue(value);
  if (!normalizedValue) return null;

  const rows = Object.values(rowsByLabel);
  return (
    rows.find((row) => normalizeLookupValue(row.email_address) === normalizedValue) ||
    rows.find((row) => normalizeLookupValue(row.name) === normalizedValue) ||
    rows.find((row) => normalizeLookupValue(formatLookupLabel(row)) === normalizedValue) ||
    null
  );
};

const parseDateOnly = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const isoDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const preserveViewportPosition = () => {
  if (typeof window === 'undefined') return;
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) return;

  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  window.requestAnimationFrame(() => {
    window.scrollTo(scrollX, scrollY);
  });
};

const getRewardIneligibilityError = (row: StaffLookupRow): string | null => {
  if (row.grade.toUpperCase().startsWith('SCS')) return SCS_REWARD_ERROR;
  if (!row.staff_number) return MISSING_EMPLOYEE_IDENTIFIER_ERROR;
  if (row.contract_type.toLowerCase() === 'contingent labour') {
    return NON_CIVIL_SERVANT_ERROR;
  }

  const leavingDate = parseDateOnly(row.leaving_date);
  if (!leavingDate) return null;

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return leavingDate.getTime() < todayStart.getTime() ? LEFT_BUSINESS_ERROR : null;
};

export const toStaffLookupRow = (row: AthenaLookupRow): StaffLookupRow | null => {
  const staff_number = toNonEmptyString(row.staff_number);
  const cost_centre = toNonEmptyString(row.cost_centre);
  const name = toNonEmptyString(row.name);
  const grade = toNonEmptyString(row.grade);
  const email_address = toNonEmptyString(row.email_address);
  const directorate = toNonEmptyString(row.directorate);
  const contract_type = toNonEmptyString(row.contract_type);
  const line_manager_email = toNonEmptyString(row.line_manager_email);
  const leaving_date = toNonEmptyString(row.leaving_date);

  if (!name || !email_address) return null;

  return {
    staff_number,
    cost_centre,
    name,
    grade,
    email_address,
    directorate,
    contract_type,
    line_manager_email,
    leaving_date,
  };
};

const hasDuplicateTeamNomineeEmail = (
  questionName: string,
  nomineeEmail: string,
  values: Record<string, unknown>,
): boolean => {
  const prefix = getInstancePrefix(questionName);
  if (!prefix) return false;

  const normalizedNomineeEmail = nomineeEmail.trim().toLowerCase();
  if (!normalizedNomineeEmail) return false;

  const currentRowNomineeEmailField = `${prefix}nominee-email`;

  return Object.entries(values).some(([key, value]) => {
    if (key === currentRowNomineeEmailField) return false;
    if (!/^\d+-nominee-email$/.test(key)) return false;
    return toNonEmptyString(value).toLowerCase() === normalizedNomineeEmail;
  });
};

export default function AthenaStaffLookup({
  question,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { fetchJWT } = useAuthContext();
  const { setValue, clearErrors, getValues } = useFormContext();
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupHint, setLookupHint] = useState<string | null>(null);
  const [lookupSuccess, setLookupSuccess] = useState<string | null>(null);
  const [showGoodChoiceConfetti, setShowGoodChoiceConfetti] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const rowsByLabelRef = useRef<Record<string, StaffLookupRow>>({});
  const latestSearchRequestId = useRef(0);
  const pendingConfirmedValueRef = useRef('');

  const targetFields = useMemo(
    () => getLookupTargetFields(question.name),
    [question.name],
  );

  useEffect(() => {
    if (lookupSuccess !== GOOD_CHOICE_MESSAGE) {
      setShowGoodChoiceConfetti(false);
      return;
    }

    setShowGoodChoiceConfetti(true);
    const timerId = window.setTimeout(() => {
      setShowGoodChoiceConfetti(false);
    }, GOOD_CHOICE_CONFETTI_DURATION_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [lookupSuccess]);

  const clearTargetFields = useCallback(() => {
    setValue(targetFields.staffNumber, '', { shouldDirty: false, shouldValidate: false });
    setValue(targetFields.costCentre, '', { shouldDirty: false, shouldValidate: false });
    setValue(targetFields.fullName, '', { shouldDirty: false, shouldValidate: true });
    setValue(targetFields.email, '', { shouldDirty: false, shouldValidate: true });
    setValue(targetFields.grade, '', { shouldDirty: false, shouldValidate: true });
    setValue(targetFields.function, '', { shouldDirty: false, shouldValidate: true });
    setValue(targetFields.lineManagerEmail, '', { shouldDirty: false, shouldValidate: true });
    setValue(targetFields.dataCorrect, '', { shouldDirty: false, shouldValidate: false });
    setValue(targetFields.incorrectReason, '', { shouldDirty: false, shouldValidate: false });
  }, [setValue, targetFields]);

  const clearLookupSelection = useCallback(
    (shouldValidate = false) => {
      setValue(question.name, '', { shouldDirty: false, shouldValidate });
      if (!shouldValidate) {
        clearErrors(question.name);
      }
    },
    [clearErrors, question.name, setValue],
  );

  const populateTargetFields = useCallback(
    (row: StaffLookupRow) => {
      preserveViewportPosition();
      setValue(targetFields.staffNumber, row.staff_number, {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue(targetFields.costCentre, row.cost_centre, {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue(targetFields.fullName, row.name, { shouldDirty: false, shouldValidate: true });
      setValue(targetFields.email, row.email_address, { shouldDirty: false, shouldValidate: true });
      setValue(targetFields.grade, row.grade, { shouldDirty: false, shouldValidate: true });
      setValue(targetFields.function, row.directorate, { shouldDirty: false, shouldValidate: true });
      setValue(targetFields.lineManagerEmail, row.line_manager_email, {
        shouldDirty: false,
        shouldValidate: true,
      });
      clearErrors([
        question.name,
        targetFields.costCentre,
        targetFields.fullName,
        targetFields.email,
        targetFields.grade,
        targetFields.function,
        targetFields.lineManagerEmail,
      ]);
      preserveViewportPosition();
      scrollToFieldGroup(targetFields.fullName);
    },
    [clearErrors, question.name, setValue, targetFields],
  );

  const applySelectedLookupRow = useCallback(
    (selectedRow: StaffLookupRow) => {
      pendingConfirmedValueRef.current = '';

      const ineligibilityError = getRewardIneligibilityError(selectedRow);
      if (ineligibilityError) {
        setLookupSuccess(null);
        clearTargetFields();
        clearLookupSelection(true);
        setLookupHint(null);
        setLookupError(ineligibilityError);
        return;
      }

      const nominatorEmail = toNonEmptyString(getValues('nominator-email')).toLowerCase();
      const submitterEmail = toNonEmptyString(getValues('submitter-email')).toLowerCase();
      const nomineeEmail = toNonEmptyString(selectedRow.email_address).toLowerCase();
      const blockedNominationEmails = [nominatorEmail, submitterEmail].filter(Boolean);
      if (blockedNominationEmails.includes(nomineeEmail)) {
        setLookupSuccess(null);
        clearTargetFields();
        clearLookupSelection(true);
        setLookupHint(null);
        setLookupError(SELF_NOMINATION_ERROR);
        return;
      }

      if (
        hasDuplicateTeamNomineeEmail(
          question.name,
          nomineeEmail,
          getValues() as Record<string, unknown>,
        )
      ) {
        setLookupSuccess(null);
        clearTargetFields();
        clearLookupSelection(true);
        setLookupHint(null);
        setLookupError(DUPLICATE_NOMINEE_ERROR);
        return;
      }

      setLookupError(null);
      setLookupHint(null);
      setLookupSuccess(
        normalizeLookupValue(selectedRow.email_address) === GOOD_CHOICE_EMAIL
          ? GOOD_CHOICE_MESSAGE
          : null,
      );
      populateTargetFields(selectedRow);
    },
    [clearLookupSelection, clearTargetFields, getValues, populateTargetFields, question.name],
  );

  const searchStaff = useCallback(
    async (query: string): Promise<string[]> => {
      const trimmedQuery = query.trim();
      const requestId = latestSearchRequestId.current + 1;
      latestSearchRequestId.current = requestId;

      if (trimmedQuery.length < MINIMUM_SEARCH_LENGTH) {
        rowsByLabelRef.current = {};
        setIsSearching(false);
        setLookupError(null);
        setLookupHint(
          `Enter at least ${MINIMUM_SEARCH_LENGTH} characters to search by name or email`,
        );
        setLookupSuccess(null);
        clearTargetFields();
        clearLookupSelection(false);
        return [];
      }

      setLookupError(null);
      setLookupHint(null);
      setLookupSuccess(null);
      setIsSearching(true);

      try {
        const { idToken } = await fetchJWT({ redirectOnFail: false });
        if (!idToken) {
          throw new Error('Missing auth token');
        }

        const response = await queryAthenaLookup(idToken, {
          lookupType: 'staffInPost',
          environment: getRuntimeEnvironment(),
          searchTerm: trimmedQuery,
          maxRows: LOOKUP_RESULT_LIMIT,
        });

        const rows = response.rows
          .map(toStaffLookupRow)
          .filter((row): row is StaffLookupRow => row !== null);

        if (latestSearchRequestId.current !== requestId) {
          return [];
        }

        const rowsByLabel = rows.reduce<Record<string, StaffLookupRow>>((acc, row) => {
          acc[formatLookupLabel(row)] = row;
          return acc;
        }, {});
        rowsByLabelRef.current = {
          ...rowsByLabelRef.current,
          ...rowsByLabel,
        };

        const pendingConfirmedValue = pendingConfirmedValueRef.current;
        if (pendingConfirmedValue) {
          const pendingRow = resolveSelectedLookupRow(
            pendingConfirmedValue,
            rowsByLabelRef.current,
          );
          if (pendingRow) {
            applySelectedLookupRow(pendingRow);
          }
        }

        if (rows.length === 0) {
          clearTargetFields();
          clearLookupSelection(false);
          setLookupHint('No matching staff records found');
        }

        return rows.map(formatLookupLabel);
      } catch (_error) {
        rowsByLabelRef.current = {};
        clearTargetFields();
        clearLookupSelection(false);
        setLookupHint(null);
        setLookupSuccess(null);
        setLookupError('Staff lookup is unavailable. Please try again later.');
        return [];
      } finally {
        if (latestSearchRequestId.current === requestId) {
          setIsSearching(false);
        }
      }
    },
    [applySelectedLookupRow, clearLookupSelection, clearTargetFields, fetchJWT],
  );

  const handleValueConfirmed = useCallback(
    (value: string) => {
      const selectedRow = resolveSelectedLookupRow(value, rowsByLabelRef.current);
      if (!selectedRow) {
        if (isSearching) {
          pendingConfirmedValueRef.current = value;
          setLookupHint('Completing staff lookup...');
          return;
        }
        setLookupSuccess(null);
        clearTargetFields();
        clearLookupSelection(true);
        return;
      }

      applySelectedLookupRow(selectedRow);
    },
    [applySelectedLookupRow, clearLookupSelection, clearTargetFields, isSearching],
  );

  return (
    <div className={lookupError ? 'govuk-form-group govuk-form-group--error' : 'govuk-form-group'}>
      <AutocompleteWrapper
        id={anchorId || String(question.name)}
        name={question.name}
        title={question.title}
        rules={question.rules}
        suppressErrorMessage={Boolean(lookupError)}
        placeholder={question.searchPlaceholder || question.placeholder}
        detailsHeading={detailsHeading}
        detailsContent={detailsContent}
        matchLimit={question.matchLimit ?? DEFAULT_MATCH_LIMIT}
        fuzzyMatch={question.fuzzyMatch ?? true}
        asyncSearch={searchStaff}
        isLoading={isSearching}
        loadingMessage={LOOKUP_LOADING_MESSAGE}
        minimumLoadingMessageLength={MINIMUM_SEARCH_LENGTH}
        onValueConfirmed={handleValueConfirmed}
      />
      {lookupHint && <p className="govuk-hint govuk-!-margin-top-2">{lookupHint}</p>}
      {lookupSuccess && (
        <CelebrationMessage>
          {showGoodChoiceConfetti && (
            <CelebrationConfetti data-testid="good-choice-confetti" aria-hidden="true">
              {GOOD_CHOICE_CONFETTI_PARTICLES.map((particle, index) => (
                <CelebrationConfettiPiece
                  key={`${particle.left}-${index}`}
                  $left={particle.left}
                  $color={particle.color}
                  $width={particle.width}
                  $height={particle.height}
                  $delayMs={particle.delayMs}
                  $driftPx={particle.driftPx}
                  $spinDeg={particle.spinDeg}
                />
              ))}
            </CelebrationConfetti>
          )}
          <p className="govuk-body govuk-!-margin-top-2 govuk-!-margin-bottom-0">{lookupSuccess}</p>
        </CelebrationMessage>
      )}
      {lookupError && <p className="govuk-error-message govuk-!-margin-top-2">{lookupError}</p>}
    </div>
  );
}

